/**
 * Security & Compliance Page
 *
 * Trust/security overview for Customer Portal users. Displays the platform's
 * security posture — encryption, access controls, data isolation, and
 * infrastructure hardening — in customer-facing language (no AWS service names).
 */

import { useEffect, useState } from 'react';
import {
  ShieldCheck,
  Lock,
  KeyRound,
  Database,
  Server,
  Globe,
  Archive,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { customerApiClient } from '@/services/api';
import { Skeleton } from '@connor-music/shared';
import type { CustomerSecurityOverview } from '@shared/types';

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

function HighlightCard({
  icon: Icon,
  iconBg,
  iconColor,
  title,
  description,
  active,
}: {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  title: string;
  description: string;
  active: boolean;
}) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div
          className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg ${iconBg}`}
        >
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            {active && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-50 dark:bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-3 w-3" />
                Active
              </span>
            )}
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

function DetailSection({
  icon: Icon,
  iconColor,
  borderColor,
  title,
  items,
}: {
  icon: React.ElementType;
  iconColor: string;
  borderColor: string;
  title: string;
  items: string[];
}) {
  return (
    <div className={`border-l-4 ${borderColor} pl-6`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-5 w-5 ${iconColor}`} />
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
      </div>
      <ul className="space-y-2.5">
        {items.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2.5">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500 dark:text-green-400" />
            <span className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PlannedItem({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500 dark:text-blue-400" />
        <div>
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              {title}
            </h4>
            <span className="inline-flex rounded-full bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-400">
              Planned
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Loading skeleton                                                          */
/* -------------------------------------------------------------------------- */

function SecuritySkeleton() {
  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero skeleton */}
      <div className="text-center mb-12">
        <div className="flex justify-center mb-4">
          <Skeleton className="h-16 w-16 rounded-2xl" />
        </div>
        <Skeleton className="h-8 w-64 mx-auto" />
        <Skeleton className="mt-3 h-5 w-full max-w-lg mx-auto" />
        <Skeleton className="mt-2 h-5 w-full max-w-md mx-auto" />
      </div>

      {/* Highlight cards skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm"
          >
            <div className="flex items-start gap-4">
              <Skeleton className="h-11 w-11 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Detail sections skeleton */}
      <div className="space-y-8">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="border-l-4 border-gray-200 dark:border-gray-700 pl-6">
            <Skeleton className="h-5 w-48 mb-3" />
            <div className="space-y-2.5">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main page component                                                       */
/* -------------------------------------------------------------------------- */

export default function Security() {
  const [overview, setOverview] = useState<CustomerSecurityOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadOverview = async () => {
      try {
        const data = await customerApiClient.getSecurityOverview();
        setOverview(data);
      } catch (error) {
        console.error('Failed to load security overview:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadOverview();
  }, []);

  if (isLoading) {
    return <SecuritySkeleton />;
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* ------------------------------------------------------------------ */}
      {/*  Hero Header                                                       */}
      {/* ------------------------------------------------------------------ */}
      <div className="text-center mb-12">
        <div className="flex justify-center mb-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-green-100 dark:bg-green-900/30">
            <ShieldCheck className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Security & Compliance
        </h1>
        <p className="mt-3 text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
          Your audit data is protected by enterprise-grade security at every layer
          — from encryption at rest to isolated storage, we safeguard the
          confidentiality and integrity of your sensitive streaming data.
        </p>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/*  Highlight Cards (2x2)                                             */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
        <HighlightCard
          icon={Lock}
          iconBg="bg-green-100 dark:bg-green-900/30"
          iconColor="text-green-600 dark:text-green-400"
          title="End-to-End Encryption"
          description="AES-256 encryption at rest for all stored files. TLS 1.2+ encryption for all data in transit. Server-side encryption is enforced at the storage policy level."
          active={
            overview !== null &&
            overview.encryption_at_rest &&
            overview.encryption_in_transit
          }
        />
        <HighlightCard
          icon={KeyRound}
          iconBg="bg-blue-100 dark:bg-blue-900/30"
          iconColor="text-blue-600 dark:text-blue-400"
          title="Strict Access Control"
          description="Multi-factor authentication required for all accounts. Time-limited, cryptographically signed URLs for file access. Per-user download permissions controlled by your audit administrator."
          active={
            overview !== null &&
            overview.mfa_enforced &&
            overview.download_controls
          }
        />
        <HighlightCard
          icon={Database}
          iconBg="bg-purple-100 dark:bg-purple-900/30"
          iconColor="text-purple-600 dark:text-purple-400"
          title="Dedicated Data Isolation"
          description="Every audit project receives its own dedicated storage bucket with independent access controls. Your data is physically separated from all other projects."
          active={
            overview !== null &&
            overview.bucket_isolation &&
            overview.public_access_blocked
          }
        />
        <HighlightCard
          icon={Server}
          iconBg="bg-amber-100 dark:bg-amber-900/30"
          iconColor="text-amber-600 dark:text-amber-400"
          title="Hardened Infrastructure"
          description="Serverless architecture with no persistent servers to compromise. Separate cloud environments for development, testing, and production. HTTPS enforced on all connections."
          active={
            overview !== null &&
            overview.serverless_architecture &&
            overview.environment_isolation
          }
        />
      </div>

      {/* ------------------------------------------------------------------ */}
      {/*  Detailed Sections                                                 */}
      {/* ------------------------------------------------------------------ */}
      <div className="space-y-8 mb-16">
        <DetailSection
          icon={Lock}
          iconColor="text-green-600 dark:text-green-400"
          borderColor="border-green-200 dark:border-green-800"
          title="Encryption at Rest"
          items={[
            'AES-256 server-side encryption applied to every file before storage',
            'Encryption enforced at the storage policy level \u2014 unencrypted uploads are rejected',
            'File versioning enabled for data integrity protection',
          ]}
        />

        <DetailSection
          icon={Globe}
          iconColor="text-blue-600 dark:text-blue-400"
          borderColor="border-blue-200 dark:border-blue-800"
          title="Encryption in Transit"
          items={[
            'TLS 1.2+ for all API and file transfer connections',
            'HTTP/2 and HTTP/3 support via content delivery network',
            'Automatic HTTP-to-HTTPS redirect enforced',
          ]}
        />

        <DetailSection
          icon={KeyRound}
          iconColor="text-indigo-600 dark:text-indigo-400"
          borderColor="border-indigo-200 dark:border-indigo-800"
          title="Access Control"
          items={[
            'Multi-factor authentication required for all customer accounts',
            'Time-limited, cryptographically signed URLs for all file uploads and downloads',
            'Per-user download permissions managed by audit administrators',
            'No direct cloud credentials are ever shared with external users',
          ]}
        />

        <DetailSection
          icon={Database}
          iconColor="text-purple-600 dark:text-purple-400"
          borderColor="border-purple-200 dark:border-purple-800"
          title="Project Isolation"
          items={[
            'Each audit project receives its own dedicated, isolated storage bucket',
            'Access scoped through signed, time-limited URLs specific to each project',
            'All public access permanently blocked at the infrastructure level',
          ]}
        />

        <DetailSection
          icon={Archive}
          iconColor="text-amber-600 dark:text-amber-400"
          borderColor="border-amber-200 dark:border-amber-800"
          title="Data Lifecycle"
          items={[
            'Active data stored in high-performance, highly durable cloud storage',
            `Automatic transition to long-term archival storage after engagement completion${overview && overview.glacier_archival_days > 0 ? ` (${overview.glacier_archival_days} days)` : ''}`,
            'Secure deletion with configurable retention policies',
          ]}
        />
      </div>

      {/* ------------------------------------------------------------------ */}
      {/*  Planned Enhancements                                              */}
      {/* ------------------------------------------------------------------ */}
      <div>
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Coming Soon
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Planned security enhancements on our roadmap.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PlannedItem
            title="Web Application Firewall"
            description="Advanced threat protection and request filtering"
          />
          <PlannedItem
            title="Security Headers"
            description="Content Security Policy, HSTS, and frame protection"
          />
          <PlannedItem
            title="SOC 2 Compliance"
            description="Independent audit of security controls and processes"
          />
          <PlannedItem
            title="Vulnerability Scanning"
            description="Automated dependency and code security analysis"
          />
        </div>
      </div>
    </div>
  );
}
